import type { Map as MapboxMap } from "mapbox-gl";
import type { LocationFilter } from "@/components/WebLocationSearch.vue";
import { fitMapToBbox, MAP_POINT_ZOOM } from "@/utils/map";
import { bottomSlotFramingInset } from "@carnet/ui/utils/mapFraming";

type FocusStrategy = (map: MapboxMap, filter: LocationFilter, animate: boolean) => void;

const fitToLocation: FocusStrategy = (map, filter, animate) => {
  if (filter.bbox) {
    fitMapToBbox(map, filter.bbox, animate);
  } else if (filter.center) {
    map.flyTo({
      center: filter.center,
      zoom: MAP_POINT_ZOOM,
      padding: { top: 0, right: 0, bottom: bottomSlotFramingInset(map.getContainer(), 0), left: 0 },
      animate,
      duration: animate ? 600 : 0,
    });
  }
};

export function useMapLocationFocus(getMap: () => MapboxMap | undefined, filter: Ref<LocationFilter>, focus: FocusStrategy = fitToLocation) {
  function focusOnLocation(animate: boolean) {
    const map = getMap();
    if (map) focus(map, filter.value, animate);
  }

  const coordinateKey = computed(() => {
    const center = filter.value.center;
    return center ? `${center[0]},${center[1]},${filter.value.radius ?? ""}` : "";
  });

  watch(coordinateKey, () => focusOnLocation(true));

  return { focusOnLocation };
}
